import React from 'react';

import PropTypes from 'prop-types';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import styled from 'styled-components';

import { FormattedMessage, FormattedHTMLMessage } from '@techstyle/react-intl';

import { mobile } from '../../styles';
import { Header } from '../../styles/header';

const DescriptionContainer = styled.div`
  .react-tabs {
    display: flex;
    flex-direction: column;
    width: 100%;
    .react-tabs__tab-list {
      display: flex;
      align-items: center;
      margin-bottom: ${props => props.theme.spacing.small}px;
      padding-bottom: ${props => props.theme.spacing.tiny + 2}px;
      border-bottom: 2px solid ${props => props.theme.colors.default};
      .react-tabs__tab {
        &:hover {
          cursor: pointer;
        }
      }
      .react-tabs__tab span {
        color: ${props => props.theme.colors.gray500};
      }
      .react-tabs__tab--selected span {
        color: ${props => props.theme.colors.default};
      }
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
`;

const Title = styled(Header).attrs({
  level: 6,
})`
  margin-right: 24px;
  ${mobile`
    display: none;
    `};

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
`;

const Shipping = styled(Header).attrs({
  level: 6,
})`
  font-size: 14px;
  ${mobile`
    display:none;
  `};

  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles}
`;

const DescriptionContent = styled.div`
  ${({ theme }) => theme.paragraph.variants.paragraph4.textStyles}

  > ul {
    padding-left: ${props => props.theme.spacing.small}px;
    list-style: outside;
  }
`;

const LongDescription = styled.p`
  margin-bottom: ${props => props.theme.spacing.small}px;
`;

const DetailsList = styled.ul``;

function ProductDescription({
  className,
  details,
  isBorderfreeCustomer,
  shouldShowShippingExchange = true,
  longDescription,
  tabs = false,
  isGwpQuickView = false,
  isSet = false,
}) {
  const renderDetails = () => {
    return (
      <>
        {longDescription ? (
          <LongDescription>{longDescription}</LongDescription>
        ) : null}
        <DetailsList>
          {(details || '').split(/[\r\n]+/g).map((detail, i) => (
            <li key={i}>{detail}</li>
          ))}
        </DetailsList>
      </>
    );
  };

  const renderTitle = () => (
    <Title>
      <FormattedMessage
        id="site_pdp.product_details_title"
        defaultMessage="Details"
      />
    </Title>
  );

  const renderShipping = () => (
    <Shipping>
      <FormattedMessage
        id="site_pdp.shipping_exchanges_returns"
        defaultMessage="Shipping, Exchanges & Returns"
      />
    </Shipping>
  );

  const renderTitleContainer = () => {
    switch (true) {
      case isSet || isGwpQuickView:
        return (
          <TitleContainer data-autotag="mobile-pdp-product-details">
            <DescriptionContent>{renderDetails()}</DescriptionContent>
          </TitleContainer>
        );
      case tabs:
        return (
          <TitleContainer>
            <Tabs>
              <TabList>
                <Tab>{renderTitle()}</Tab>
                {shouldShowShippingExchange && !isBorderfreeCustomer ? (
                  <Tab>{renderShipping()}</Tab>
                ) : null}
              </TabList>
              <TabPanel data-autotag="desktop-pdp-product-details">
                <DescriptionContent>{renderDetails()}</DescriptionContent>
              </TabPanel>
              {shouldShowShippingExchange ? (
                <TabPanel>
                  <FormattedHTMLMessage
                    id="site_pdp.shipping_return_guarantee_html"
                    defaultMessage="If you’re not completely satisfied with your SavageX.com or Savage X Fenty retail store purchase, Rewards Members can return their items for free within 90 days of purchase (30 days for Guests). Both online and in-store purchases may be returned in-store as long as all original tags are attached. 

Any item to be returned or exchanged must be in new, unused and resalable condition, with original tags attached. Any return or exchange should be sent back in the original, undamaged box and packaging, plus any accessories or extras that may have been included. Exchanges are subject to size and style availability. 

Please note: All Sale items are final sale and are not eligible for returns or exchanges. Exclusions and limitations apply."
                  />
                </TabPanel>
              ) : null}
            </Tabs>
          </TitleContainer>
        );
      default:
        return (
          <TitleContainer data-autotag="mobile-pdp-product-details">
            {renderTitle()}
            {renderShipping()}
            <DescriptionContent>{renderDetails()}</DescriptionContent>
          </TitleContainer>
        );
    }
  };

  return (
    <DescriptionContainer className={className}>
      {renderTitleContainer()}
    </DescriptionContainer>
  );
}

ProductDescription.propTypes = {
  className: PropTypes.string,
  details: PropTypes.string.isRequired,
  isBorderfreeCustomer: PropTypes.bool,
  shouldShowShippingExchange: PropTypes.bool,
  longDescription: PropTypes.string,
  tabs: PropTypes.bool,
  isGwpQuickView: PropTypes.bool,
  isSet: PropTypes.bool,
};

ProductDescription.defaultProps = {
  tabs: false,
  shouldShowShippingExchange: true,
  isSet: false,
};

ProductDescription.DescriptionContent = DescriptionContent;

export default ProductDescription;
